import React from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import RenderCMSContent from '../RenderCMSContent';
import EventVariants from '../EventDetail/EventVariants';
import MunlightConcertCard from './MunlightConcertCard';
import { MUNLIGHT_CONCERTS, MUNLIGHT_INTRO_PARAGRAPHS } from './concertsData';
import { MUNLIGHT, MUNLIGHT_BOOKING_URL } from './munlightTheme';

const strapiBase = () => process.env.REACT_APP_STRAPI_API_URL || '';

function thumbnailSrc(event) {
  const url = event?.thumbnail?.url;
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${strapiBase()}${url}`;
}

function hasAnyCmsBlock(event) {
  return Boolean(
    event?.longDescription?.length ||
      event?.longDescription2?.length ||
      event?.longDescription3?.length ||
      event?.longDescription4?.length
  );
}

/**
 * Landing dedicata per l'evento speciale Munlight Sonata (/event/munlight-sonata).
 * Palette allineata al volantino A3 (bianco caldo, ardesia, viola orchidea).
 */
function MunlightSonata({
  event,
  userData,
  onOpenDrawer,
  isSoldOut,
  allParticipantsPaidInFull,
  allVarianti,
  registrationOpen,
  isEarlyAccessOnly,
}) {
  const img = thumbnailSrc(event);

  return (
    <Box minH="100vh" overflowX="hidden">
      {/* Sezione chiara: hero + introduzione (come colonna sinistra del PDF) */}
      <Box bg={MUNLIGHT.bgLight} color={MUNLIGHT.textInk}>
        <Box
          position="relative"
          minH={{ base: '56vh', md: '62vh' }}
          display="flex"
          alignItems="flex-end"
          pb={{ base: 12, md: 16 }}
          bg={MUNLIGHT.bgLight}
          background={
            img
              ? `linear-gradient(to top, ${MUNLIGHT.bgLight} 0%, rgba(255,254,242,0.94) 32%, rgba(255,254,242,0.65) 100%), url(${img})`
              : undefined
          }
          backgroundSize="cover"
          backgroundPosition="center"
          sx={
            !img
              ? {
                  backgroundImage: `radial-gradient(ellipse 90% 60% at 50% 100%, ${MUNLIGHT.accentSoft}33 0%, transparent 55%)`,
                }
              : undefined
          }
        >
          <Container maxW="container.lg" position="relative" zIndex={1}>
            <VStack align="stretch" spacing={4}>
              <Text
                fontSize="sm"
                letterSpacing="0.28em"
                textTransform="uppercase"
                color={MUNLIGHT.accent}
                fontWeight="600"
              >
                Rassegna musicale
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: '2.75rem', md: '4rem' }}
                fontWeight="700"
                fontStyle="italic"
                lineHeight="1.05"
                fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                letterSpacing="-0.03em"
              >
                <Box as="span" color={MUNLIGHT.textInk}>
                  Munlight
                </Box>
                <br />
                <Box as="span" color={MUNLIGHT.accent}>
                  Sonata
                </Box>
              </Heading>
              <Text
                fontSize={{ base: 'md', md: 'xl' }}
                color={MUNLIGHT.textMuted}
                maxW="2xl"
                lineHeight="tall"
                fontWeight="500"
              >
                Rassegna musicale al Munlab
              </Text>
              <Button
                as="a"
                href={MUNLIGHT_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                alignSelf="flex-start"
                size="lg"
                mt={2}
                bg={MUNLIGHT.accent}
                color={MUNLIGHT.white}
                fontWeight="600"
                _hover={{ bg: MUNLIGHT.accentHover }}
                _active={{ bg: MUNLIGHT.accent }}
              >
                Prenota
              </Button>
            </VStack>
          </Container>
        </Box>

        <Container
          maxW="container.lg"
          py={{ base: 12, md: 16 }}
          px={{ base: 4, md: 6 }}
        >
          <VStack align="stretch" spacing={{ base: 10, md: 14 }}>
            <Box as="section" aria-labelledby="munlight-intro-heading">
              <Heading
                as="h2"
                id="munlight-intro-heading"
                fontSize="sm"
                fontWeight="700"
                letterSpacing="0.2em"
                textTransform="uppercase"
                color={MUNLIGHT.accent}
                mb={8}
              >
                La rassegna
              </Heading>
              <VStack align="stretch" spacing={6} maxW="3xl">
                {MUNLIGHT_INTRO_PARAGRAPHS.map((paragraph) => (
                  <Text
                    key={paragraph}
                    fontSize={{ base: 'md', md: 'lg' }}
                    lineHeight="tall"
                    color={MUNLIGHT.textInk}
                  >
                    {paragraph}
                  </Text>
                ))}
              </VStack>
            </Box>

            {hasAnyCmsBlock(event) ? (
              <Box
                color={MUNLIGHT.textMuted}
                fontSize={{ base: 'md', md: 'lg' }}
                lineHeight="tall"
                sx={{ '& p': { marginBottom: '1rem' } }}
              >
                <RenderCMSContent content={event?.longDescription} hideTitle />
                <RenderCMSContent content={event?.longDescription2} hideTitle />
                <RenderCMSContent content={event?.longDescription3} hideTitle />
                <RenderCMSContent content={event?.longDescription4} hideTitle />
              </Box>
            ) : null}
          </VStack>
        </Container>
      </Box>

      {/* Sezione scura: concerti (come colonna destra del PDF) */}
      <Box
        as="section"
        aria-labelledby="munlight-concerts-heading"
        bg={MUNLIGHT.bgSlate}
        color={MUNLIGHT.white}
        pb={{ base: 12, md: 16 }}
        pt={{ base: 10, md: 14 }}
      >
        <Container maxW="container.lg" px={{ base: 4, md: 6 }}>
          <Heading
            as="h2"
            id="munlight-concerts-heading"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="600"
            fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            color={MUNLIGHT.accentSoft}
            mb={{ base: 8, md: 10 }}
            letterSpacing="-0.02em"
          >
            I concerti
          </Heading>
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: 8, md: 10 }}
          >
            {MUNLIGHT_CONCERTS.map((concert) => (
              <MunlightConcertCard key={concert.id} concert={concert} />
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {event?.Varianti?.length > 0 ? (
        <Box
          id="prenotazioni"
          bg={MUNLIGHT.bgSlateDeep}
          py={8}
          position="relative"
          zIndex={1}
          scrollMarginTop={{ base: '72px', md: '120px' }}
        >
          <EventVariants
            event={event}
            handleOpenDrawer={onOpenDrawer}
            userData={userData}
            isSoldOut={isSoldOut}
            allParticipantsPaidInFull={allParticipantsPaidInFull}
            allVarianti={allVarianti}
            registrationOpen={registrationOpen}
            isEarlyAccessOnly={isEarlyAccessOnly}
          />
        </Box>
      ) : null}
    </Box>
  );
}

export default MunlightSonata;
